import Box from "@mui/material/Box";
import TransparentButton from "../CommonComponents/TransparentButton/TransparentButton";
import {DataGrid, GridColDef, GridRenderCellParams} from "@mui/x-data-grid";
import React, {useEffect, useState} from "react";
import {Organization, Role, User} from "../../types/users";
import {defaultUser, EntityType, userColumns} from "../../utils/constants";
import {useTheme} from "../../contexts/theme";
import {
    createUser,
    deleteUser,
    getOrganizations,
    getRoles,
    getUsers,
    passwordChange,
    updateUser
} from "../../queries/userQueries";
import ErrorSnackBar from "../CommonComponents/ErrorSnackBar/ErrorSnackBar";
import UserForm from "./UserForm";
import StyledPopup from "../CommonComponents/StyledPopup/StyledPopup";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import StyledConfirmationDialog from "../CommonComponents/StyledConfirmitationDialog/StyledConfirmationDialog";
import {LockReset} from "@mui/icons-material";

interface UserTableProps {
    updateEntity : EntityType.users | EntityType.organizations | "";
    handleUpdateEntity : (value: EntityType.users | EntityType.organizations | "") => void;
}

const UserTable: React.FC<UserTableProps> = ({updateEntity,handleUpdateEntity}) => {
    const theme = useTheme();
    const [users, setUsers] = useState<User[]>([])
    const [loadingUsers, setLoadingUsers] = useState<boolean>(false)
    const [roles, setRoles] = useState<Role[]>([])
    const [organizations, setOrganizations] = useState<Organization[]>([])
    const [loadingRoles, setLoadingRoles] = useState<boolean>(false)
    const [selectedUser, setSelectedUser] = useState<User>(JSON.parse(JSON.stringify(defaultUser)));
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [formMode, setFormMode] = useState<'create'| 'update' | 'passwordChange'>('create')
    const [tableColumns, setTableColumns] = useState<GridColDef<User>[]>([]);
    const [errorMessage, setErrorMessage] = useState<{error: boolean, message: string}>({
        error:false,
        message: "",
    })
    const [openDialogDeleteUser, setOpenDialogDeleteUSer] = useState<boolean>(false);


    async function loadRoles(){
        try{
            setLoadingRoles(true)
            const roles = await getRoles()
            setRoles(roles);
            setLoadingRoles(false)
        }catch (e) {
            console.error(e)
            setLoadingRoles(false)
            setErrorMessage({error: true, message: "Problems retrieving roles"})
        }
    }

    async function loadOrganizations(){
        try{
            const organizations: Organization[] = await getOrganizations()
            setOrganizations(organizations)
        }catch (e) {
            console.error(e)
            setErrorMessage({error: true, message: "Problems retrieving organizations"})
        }
    }

    async function loadUsers(){
        try{
            setLoadingUsers(true)
            const users = await getUsers()
            setUsers(users);
            setLoadingUsers(false)
        }catch (e){
            console.error(e)
            setLoadingUsers(false)
            setErrorMessage({error: true, message: "Problems retrieving users"})
        }
    }

    useEffect(() => {
        loadUsers()
        loadRoles()
        addActionColumns()
        loadOrganizations()
    }, []);

    useEffect(() => {
        if (updateEntity === EntityType.organizations) {
            loadUsers()
            loadRoles()
            addActionColumns()
            loadOrganizations()
            handleUpdateEntity("")
        }

    }, [updateEntity]);

    function onCreateClick(){
        setIsFormOpen(true)
        setFormMode("create")
    }

    async function onUpdateClick(user: User){
        setSelectedUser(user)
        setIsFormOpen(true)
        setFormMode("update")
    }

    async function onPasswordChangeClick(user: User){
        setSelectedUser(user)
        setIsFormOpen(true)
        setFormMode("passwordChange")
    }

    async function handleDeleteUser() {
        try{
            await deleteUser(selectedUser.id)
            setUsers(prevState => prevState.filter(user => user.id !== selectedUser.id))
            setErrorMessage({error: true, message: "User Deleted Successfully"})
        }catch (e) {
            console.error(e)
            setErrorMessage({error: true, message: "User Delete Failed"})
        }
    }

    function onDeleteClick(user: User){
        setSelectedUser(user);
        setOpenDialogDeleteUSer(true);
    }

    function handleClose(){
        setIsFormOpen(false);
        setSelectedUser(JSON.parse(JSON.stringify(defaultUser)));
        setFormMode("create")
    }

    function handleUserInfoChange(field: keyof User, value: string){
        setSelectedUser(prevState => ({...prevState, [field]: value}));
    }

    function handleUserRolesChange(roles: Role[]){
        setSelectedUser(prevState => ({...prevState, roles: roles}));
    }

    function handleUserOrganizationsChange(organizations: Organization[]){



        setSelectedUser(prevState => ({...prevState, organizations: organizations}));
    }

    async function handleFormSubmit(){
        try{
            if (formMode === 'create'){
                const tempSelectedUser = {...selectedUser};
                const createdUserId: {id: string} = await createUser(selectedUser);
                tempSelectedUser.id = createdUserId.id;
                tempSelectedUser.password = ""
                setUsers(prevState => [...prevState, JSON.parse(JSON.stringify(tempSelectedUser))])
            }else if (formMode === 'update'){
                await updateUser(selectedUser, selectedUser.id)
                const tempUsers = [...users]
                const updatedUserIndex = tempUsers.findIndex(user => user.id === selectedUser.id)
                if (updatedUserIndex > -1) {
                   tempUsers[updatedUserIndex] = (selectedUser)
                    setUsers(tempUsers)
                }
            }else{
                await passwordChange(selectedUser.id, selectedUser.password!)
                const tempUsers = [...users]
                const updatedUserIndex = tempUsers.findIndex(user => user.id === selectedUser.id)
                if (updatedUserIndex > -1) {
                    tempUsers[updatedUserIndex] = (selectedUser)
                    setUsers(tempUsers)
                }
            }
            handleUpdateEntity(EntityType.users)
            handleClose()
            setErrorMessage({error: true, message: `User ${formMode === 'create' ? 'created' : formMode === 'passwordChange' ? 'password changed' : 'updated'}  successfully`})
        }catch (e: any) {
            if(e.response && e.response.data && e.response.data.detail){
                console.error(e)
                setErrorMessage({error: true, message: `Error ${formMode === 'create' ? 'creating' : 'updating'} user: ${e.response.data.detail.message}`})
            }else{
                console.error(e)
                setErrorMessage({error: true, message: `Error ${formMode === 'create' ? 'creating' : 'updating'}  user`})
            }
        }
    }

    function addActionColumns(){
        const tempColumns =  [...userColumns]
        tempColumns.push(
            {
                field: "actions",
                headerName: "",
                flex: 1,
                renderCell: (params: GridRenderCellParams<User>) => (
                    <Box paddingTop={theme.spacing.xs} width={'100%'} display={'flex'} justifyContent={'space-around'} alignItems={'center'}>
                        <TransparentButton
                            onClick={() => onUpdateClick(params.row)}
                            startIcon={<EditIcon/>}
                            text={''}
                        />

                        <TransparentButton
                            onClick={() => onPasswordChangeClick(params.row)}
                            startIcon={<LockReset/>}
                            text={''}
                        />

                        <TransparentButton
                            onClick={() => onDeleteClick(params.row)}
                            startIcon={<DeleteIcon/>}
                            text={''}
                        />
                    </Box>
                ),
            },
        )
        setTableColumns(tempColumns)
    }

    return (
        <Box>
            <ErrorSnackBar  autoHideDuration={5000} open={errorMessage.error} setClose={()=>{setErrorMessage({
                error: false,
                message: ""
            })}} errorMessage={errorMessage.message} />
            <StyledConfirmationDialog
                open={openDialogDeleteUser}
                onClose={() =>  setOpenDialogDeleteUSer(false)}
                message={`Are you sure you want to delete ${selectedUser.email}?`}
                onConfirm={() => handleDeleteUser()}
                onDeny={() =>  setOpenDialogDeleteUSer(false)}
            />
            <StyledPopup
                isOpen={isFormOpen}
                onClose={handleClose}
                title={`${formMode==='create' ? 'Create User' : formMode === 'passwordChange' ? 'Password Change' : 'Update User'}`}
                fullWidth={false}
                width={'65vw'}
            >
                    <UserForm
                        onInfoChange={handleUserInfoChange}
                        onRolesChange={handleUserRolesChange}
                        organizations={organizations}
                        onOrganizationsChange={handleUserOrganizationsChange}
                        user={selectedUser}
                        roles={roles}
                        mode={formMode}
                        sendFormLabel={`${formMode==='create' ? 'Create User' : formMode === 'passwordChange' ? 'Password Change' : 'Update User'}`}
                        onSubmit={handleFormSubmit}/>
            </StyledPopup>
                <TransparentButton text={'Create User'} onClick={onCreateClick}/>
                <DataGrid
                    rows={users}
                    columns={tableColumns}
                    getRowId={(row) => row.id}
                    loading={loadingUsers}
                    pageSizeOptions={[5, 25, 50]}
                    columnVisibilityModel={{
                        id:false
                    }}
                    sx={{
                        border: `2px solid ${theme.palette.white}`,
                        color: theme.palette.white,
                        backgroundColor: theme.palette.blue,
                        marginTop: theme.spacing.md,
                        '& .MuiDataGrid-main': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-columnHeader': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-row': { backgroundColor: theme.palette.blue },
                        '& .MuiDataGrid-footerContainer': { backgroundColor: theme.palette.blue },
                        '&.MuiDataGrid-root': {
                            '--DataGrid-containerBackground': theme.palette.blue,
                        },
                        '& .MuiTablePagination-root': {
                            color: theme.palette.white, // Override text color
                        },
                        '& .MuiTablePagination-selectIcon': {
                            color: theme.palette.white, // Change the color of the dropdown icon
                        },
                    }}
                />
        </Box>
    )
}

export default UserTable;